import {
  BulkCreateShiftmanagementShiftShiftsInput,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { buildAddShiftsInputParameters } from 'modules/shift_management/lib/buildAddShiftsInput'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import type DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'
import { trackAddMutationEvent } from 'modules/shift_management/lib/trackEvents'
import type { Leave, ShiftsRow } from 'modules/shift_management/lib/types'
import Shift from 'modules/shift_management/store/internal/models/Shift'

import { DaysByRow } from './reducers/selection'
import { useRefreshShiftsRows, useRefreshStats } from './refreshShiftsRows'
import { ShiftInputsAttributesByDate } from './useDefaultAttributesFromRows'
import { useSchedule } from './useSchedule'
import { useSelectionDispatch, useSelectionState } from './useSelection'
import { useShiftsRows } from './useShiftsRows'
import { useUI } from './useUI'

export const useMutateDefaultShifts = ({
  defaultAttributes,
  daysByRow,
  weekDays,
  from,
  withSearch,
  advancedOptions,
  replaceShifts,
  isBackup,
  leave,
}: {
  defaultAttributes?: ShiftInputsAttributesByDate
  daysByRow: DaysByRow
  weekDays: string
  from?: 'modal' | 'side_panel' | 'quick_actions'
  withSearch?: string
  advancedOptions?: boolean
  replaceShifts?: boolean
  isBackup?: boolean
  leave?: Leave
}) => {
  const { isEnabled: hasSelection } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()

  const { refreshShiftsRows } = useRefreshShiftsRows()
  const { refreshStats } = useRefreshStats({ fetchPolicy: 'cache-only' })

  const [addShifts, { loading }] = useMutation(ShiftManagementBulkCreateShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
  })

  const buildShiftsInput = (
    forms: DefaultShiftWithParamsForm[]
  ): BulkCreateShiftmanagementShiftShiftsInput[] => {
    const shifts = forms.flatMap((form: DefaultShiftWithParamsForm) => {
      return buildAddShiftsInputParameters({
        view,
        hasSelection,
        defaultAttributesByDate: defaultAttributes || {},
        name: form.get('title').value,
        unparsedStartTime: form.get('start_at').value,
        unparsedEndTime: form.get('end_at').value,
        daysByRow,
        defaultShiftId: form.get('id').value,
        extraHours: form.get('extra_hours').value,
        notes: form.get('notes').value,
        locationId: form.get('location_id').value,
        workAreaId: form.get('work_area_id').value,
        color: form.get('color').value,
        templateWeekId: null,
        unassignedShiftId: null,
        state: isBackup ? 'backup' : 'draft',
        leaveId: leave?.id,
        companyId: currentCompanyId,
      })
    })

    return shifts
  }

  const onSubmit = async (newForms: DefaultShiftWithParamsForm[]) => {
    const shifts = buildShiftsInput(newForms)
    const result = await addShifts({
      variables: { shifts },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      !replaceShifts &&
        trackAddMutationEvent({
          view,
          isDefaultShift: true,
          weekDays,
          shifts,
          from,
          withSearch,
          advancedOptions,
          isBackup,
          colors: newForms.map((form) => form.get('color').value),
        })

      const shiftsData = handled.data?.shifts || []
      const shiftsCreated = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId || 0,
        })
      )
      dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: shiftsCreated, view })
      dispatchUI({ type: 'REFRESHING_STATS' })
      dispatchSchedule({ type: 'ADD_SHIFTS', shifts: shiftsCreated })
      dispatchSelection({ type: 'CLEAR_SELECTION' })

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.add_shifts_success'),
      })

      // TODO: The logic here is to refresh the stats and calculate conflicts.
      // I'm not sure if we can move it to a different place and reduce responsibility.
      //
      const { employeeIds, newShiftsRowsKeys, shiftsReferenceKeys } = buildEmployeeIds(
        view,
        shiftsRows,
        shiftsCreated
      )

      if (newShiftsRowsKeys.length !== 0) {
        await refreshShiftsRows([], employeeIds)
      }

      if (newShiftsRowsKeys.length === 0) {
        // Get shiftsRows where there are affected shifts to update stats
        // This is not necessary when we have added new shiftsRows, as
        // refreshShiftsRows has already fetched the stats
        const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
          return shiftsReferenceKeys.includes(row.key)
        })

        await refreshStats(previousShiftsRows)
      }
    }

    return handled
  }

  return {
    loading,
    onSubmit,
  }
}
